import { useQuery } from "@tanstack/react-query"
import { Banknote, CircleDollarSign, Clock, Landmark, Scale, Target } from "lucide-react"

import { api } from "@/api/cliente"
import { monto } from "@/componentes/tesoreria/dinero"
import { EstadoCarga, EstadoError } from "@/componentes/ui/Estados"
import { Insignia } from "@/componentes/ui/Insignia"
import { Cabecera, Cuerpo, Fila, Tabla, TablaVacia, Td, Th } from "@/componentes/ui/Tabla"
import { TarjetaKpi } from "@/componentes/ui/TarjetaKpi"

type FilaReporte = {
  clave: string
  categoria: string
  tipo_gasto: string | null
  presupuesto: string
  tiene_partida: boolean
  ejecutado: string
  por_cancelar: string
  disponible: string | null
  ordenes: number
  cuenta_codigo?: string | null
  cuenta?: string | null
}

export type ReporteGeneral = {
  desde: string
  hasta: string
  moneda: string
  filas: FilaReporte[]
  /** Las filas sumadas por partida del plan de cuentas (28/10). */
  por_cuenta?: FilaReporte[]
  presupuesto: string
  ejecutado: string
  por_cancelar: string
  resultado: string
  proyectado: string
  nomina: string
  pagado_en_periodo: string
  pagos: number
  sin_convertir: { codigo: string; divisa: string; monto: string; motivo: string }[]
  /** Lo ejecutado en el período anterior del mismo tipo (28/10). No viene en la foto. */
  ejecutado_anterior?: string | null
}

const usd = (t: string) => [{ divisa: "USD", monto: t }]
const negativo = (t: string | null) => t !== null && Number(t) < 0

/** « (+12,3 %)» contra el período anterior; nada si el anterior fue cero. */
function variacion(r: ReporteGeneral): string {
  const antes = Number(r.ejecutado_anterior ?? 0)
  if (antes === 0) return ""
  const pct = ((Number(r.ejecutado) - antes) / antes) * 100
  return ` (${pct > 0 ? "+" : ""}${pct.toLocaleString("es-VE", { maximumFractionDigits: 1 })} %)`
}

/**
 * El reporte general de cuentas y resultados (28/10, minuta del 27/10, puntos 1 y 3): por
 * categoría, el presupuesto del período, lo ejecutado, lo que queda por cancelar y lo
 * disponible. Compone el informe y el presupuesto en el servidor
 * (`services/reporte_general.py`), así que las tres pantallas dicen la misma cifra.
 */
export function CuentasYResultados({ filtro }: { filtro: string }) {
  const consulta = useQuery({
    queryKey: ["cuentas-y-resultados", filtro],
    queryFn: () => api.get<ReporteGeneral>(`/api/v1/informes/cuentas-y-resultados?${filtro}`),
  })
  if (consulta.isPending) return <EstadoCarga etiqueta="Calculando el reporte" />
  if (consulta.isError) {
    return (
      <EstadoError
        titulo="No se pudo calcular el reporte"
        error={consulta.error}
        onReintentar={() => consulta.refetch()}
      />
    )
  }
  return <VistaCuentasYResultados r={consulta.data} />
}

/** El cuerpo del reporte, sin consulta: lo usa también la foto del viernes (`/m/<token>`). */
export function VistaCuentasYResultados({ r }: { r: ReporteGeneral }) {
  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        <TarjetaKpi
          titulo="Presupuesto del período"
          detalle="El tope mensual de cada partida, prorrateado a estos días"
          Icono={Target}
          color="var(--info)"
          cantidad={`${monto(r.presupuesto)} $`}
          montos={usd(r.presupuesto)}
        />
        <TarjetaKpi
          titulo="Gasto real (ejecutado)"
          detalle="Pagado de las facturas emitidas en el período"
          Icono={CircleDollarSign}
          color="var(--primary)"
          cantidad={`${monto(r.ejecutado)} $`}
          montos={usd(r.ejecutado)}
          pie={
            <>
              De ello, nómina: {monto(r.nomina)} $
              {r.ejecutado_anterior != null && (
                <span className="block tabular-nums">
                  Período anterior: {monto(r.ejecutado_anterior)} ${variacion(r)}
                </span>
              )}
            </>
          }
        />
        <TarjetaKpi
          titulo="Por cancelar"
          detalle="Aprobado o en aprobación que todavía no sale del banco"
          Icono={Clock}
          color="var(--warning)"
          cantidad={`${monto(r.por_cancelar)} $`}
          montos={usd(r.por_cancelar)}
        />
        <TarjetaKpi
          titulo="Resultado"
          detalle="Presupuesto menos lo ejecutado"
          Icono={Scale}
          color={negativo(r.resultado) ? "var(--destructive)" : "var(--success)"}
          cantidad={`${monto(r.resultado)} $`}
          montos={usd(r.resultado)}
          pie={`Contando lo por cancelar: ${monto(r.proyectado)} $`}
        />
        <TarjetaKpi
          titulo="Salió del banco"
          detalle="Pagos con fecha de valor en el período, de cualquier factura"
          Icono={Landmark}
          color="var(--fg-muted)"
          cantidad={`${monto(r.pagado_en_periodo)} $`}
          montos={usd(r.pagado_en_periodo)}
          pie={`${r.pagos} pago${r.pagos === 1 ? "" : "s"}`}
        />
        <TarjetaKpi
          titulo="Categorías con movimiento"
          detalle="Con gasto, compromiso o partida en el período"
          Icono={Banknote}
          color="var(--fg-muted)"
          cantidad={r.filas.length}
        />
      </div>

      {(r.por_cuenta ?? []).length > 0 && (
        <Tabla etiqueta="Cuentas y resultados por partida del plan de cuentas">
          <Cabecera>
            <Th>Cuenta</Th>
            <Th className="text-right">Presupuesto</Th>
            <Th className="text-right">Ejecutado</Th>
            <Th className="text-right">Por cancelar</Th>
            <Th className="text-right">Disponible</Th>
          </Cabecera>
          <Cuerpo>
            {(r.por_cuenta ?? []).map((f) => (
              <Fila key={f.clave}>
                <Td className="tabular-nums">{f.categoria}</Td>
                <Td className="text-right tabular-nums">
                  {f.tiene_partida ? monto(f.presupuesto) : "—"}
                </Td>
                <Td className="text-right tabular-nums">{monto(f.ejecutado)}</Td>
                <Td className="text-right tabular-nums">{monto(f.por_cancelar)}</Td>
                <Td
                  className="text-right tabular-nums"
                  style={negativo(f.disponible) ? { color: "var(--destructive)" } : undefined}
                  data-exportar-valor={f.disponible === null ? "—" : undefined}
                >
                  {f.disponible === null ? "sin partida" : monto(f.disponible)}
                </Td>
              </Fila>
            ))}
          </Cuerpo>
        </Tabla>
      )}

      <Tabla etiqueta="Cuentas y resultados por categoría">
        <Cabecera>
          <Th>Categoría</Th>
          <Th className="text-right">Presupuesto</Th>
          <Th className="text-right">Ejecutado</Th>
          <Th className="text-right">Por cancelar</Th>
          <Th className="text-right">Disponible</Th>
        </Cabecera>
        <Cuerpo>
          {r.filas.length === 0 && (
            <TablaVacia colSpan={5}>Sin gasto, compromisos ni partidas en este período.</TablaVacia>
          )}
          {r.filas.map((f) => (
            <Fila key={f.clave}>
              <Td>
                {f.categoria}
                {f.cuenta_codigo && (
                  <span className="ml-1 text-xs tabular-nums" style={{ color: "var(--fg-muted)" }}>
                    {f.cuenta_codigo}
                  </span>
                )}
                {f.tipo_gasto && (
                  <span className="ml-2">
                    <Insignia>{f.tipo_gasto === "FIJO" ? "Fijo" : "Variable"}</Insignia>
                  </span>
                )}
              </Td>
              <Td className="text-right tabular-nums">
                {f.tiene_partida ? monto(f.presupuesto) : "—"}
              </Td>
              <Td className="text-right tabular-nums">{monto(f.ejecutado)}</Td>
              <Td className="text-right tabular-nums">{monto(f.por_cancelar)}</Td>
              <Td
                className="text-right tabular-nums"
                style={negativo(f.disponible) ? { color: "var(--destructive)" } : undefined}
                data-exportar-valor={f.disponible === null ? "—" : undefined}
              >
                {f.disponible === null ? "sin partida" : monto(f.disponible)}
              </Td>
            </Fila>
          ))}
          {r.filas.length > 0 && (
            <Fila>
              <Td className="font-semibold">Total (USD)</Td>
              <Td className="text-right font-semibold tabular-nums">{monto(r.presupuesto)}</Td>
              <Td className="text-right font-semibold tabular-nums">{monto(r.ejecutado)}</Td>
              <Td className="text-right font-semibold tabular-nums">{monto(r.por_cancelar)}</Td>
              <Td className="text-right font-semibold tabular-nums">{monto(r.proyectado)}</Td>
            </Fila>
          )}
        </Cuerpo>
      </Tabla>
      {r.sin_convertir.length > 0 && (
        <p className="text-xs" style={{ color: "var(--warning)" }}>
          No se pudieron convertir a dólares:{" "}
          {r.sin_convertir.map((e) => `${e.codigo} (${e.motivo})`).join(" · ")}
        </p>
      )}
    </div>
  )
}
